import type { ComponentProps, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon, type IconProps } from "~/components/icon/icon";
import { Text } from "~/components/text";
import { cn } from "~/utils";

const noticeVariants = cva(
  "flex items-start gap-3 rounded-lg border p-4 text-foreground",
  {
    variants: {
      variant: {
        caution: "border-caution-200 bg-caution-50",
        hint: "border-purple-200 bg-purple-50",
        info: "border-transparent bg-primary-25",
      },
    },
    defaultVariants: { variant: "caution" },
  },
);

const iconWrapperVariants = cva(
  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border",
  {
    variants: {
      variant: {
        caution: "border-caution-200 bg-caution-100",
        hint: "border-purple-200 bg-white",
        info: "border-transparent bg-transparent",
      },
    },
    defaultVariants: { variant: "caution" },
  },
);

const iconColorVariants = cva("", {
  variants: {
    variant: {
      caution: "text-caution-700",
      hint: "text-purple-600",
      info: "text-primary",
    },
  },
  defaultVariants: { variant: "caution" },
});

const defaultIcon: Record<"caution" | "hint" | "info", IconProps["name"]> = {
  caution: "info",
  hint: "shopping-cart",
  info: "info",
};

export const Notice = ({
  variant = "caution",
  title,
  children,
  icon,
  className,
  ...props
}: Omit<ComponentProps<"div">, "title"> &
  VariantProps<typeof noticeVariants> & {
    title?: ReactNode;
    icon?: IconProps["name"];
  }) => (
  <div
    {...props}
    role="status"
    data-variant={variant}
    className={cn(noticeVariants({ variant }), className)}
  >
    <span className={cn(iconWrapperVariants({ variant }))}>
      <Icon
        name={icon ?? defaultIcon[variant ?? "caution"]}
        size="small"
        iconClassName={cn(iconColorVariants({ variant }))}
      />
    </span>
    <div className="flex flex-col gap-0.5">
      {title != null ? <Text variant="bodyStrong">{title}</Text> : null}
      <Text variant="muted">{children}</Text>
    </div>
  </div>
);
